import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Casino as GenerateIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { DemoAccountService } from '../../services/demoAccountService';
import { ToastService } from '../../services/toastService';
import type { CreateDemoAccountRequest, CreateDemoAccountResponse } from '../../types/demoAccount';

interface CreateDemoAccountDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (result: CreateDemoAccountResponse) => void;
}

function generatePassword(length = 12): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let password = '';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  for (let i = 0; i < length; i++) {
    password += chars[array[i] % chars.length];
  }
  return password;
}

export const CreateDemoAccountDialog: React.FC<CreateDemoAccountDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isValid },
  } = useForm<CreateDemoAccountRequest>({
    defaultValues: {
      displayName: '',
      email: '',
      password: '',
      role: 'fleetManager',
      vesselCount: 3,
      entityName: '',
      mohaAccountNumber: '',
      initialEuaBalance: 5000,
      expiresInDays: 30,
    },
    mode: 'onChange',
  });

  const createMutation = useMutation({
    mutationFn: DemoAccountService.createDemoAccount,
    onSuccess: (result) => {
      ToastService.success('Demo account created successfully', 'Account Created');
      reset();
      onSuccess(result);
    },
    onError: (error: unknown) => {
      ToastService.handleApiError(error, 'Create Demo Account');
    },
  });

  const onSubmit = (data: CreateDemoAccountRequest) => {
    createMutation.mutate(data);
  };

  const handleClose = () => {
    if (!createMutation.isPending) {
      reset();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Demo Account</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Controller
            name="displayName"
            control={control}
            rules={{ required: 'Display name is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Demo User Name"
                fullWidth
                error={!!errors.displayName}
                helperText={errors.displayName?.message}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            rules={{
              required: 'Email is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Invalid email format',
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email Address"
                type="email"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />

          <Controller
            name="password"
            control={control}
            rules={{
              required: 'Password is required',
              minLength: {
                value: 6,
                message: 'Password must be at least 6 characters',
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                error={!!errors.password}
                helperText={errors.password?.message}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Auto-generate password">
                          <IconButton
                            onClick={() => {
                              const pw = generatePassword();
                              setValue('password', pw, {
                                shouldValidate: true,
                              });
                              setShowPassword(true);
                            }}
                            edge="end"
                            size="small"
                          >
                            <GenerateIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />

          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <FormControl>
                <FormLabel>Role</FormLabel>
                <RadioGroup row {...field}>
                  <FormControlLabel
                    value="fleetManager"
                    control={<Radio />}
                    label="Fleet Manager"
                  />
                  <FormControlLabel value="trader" control={<Radio />} label="Trader" />
                </RadioGroup>
              </FormControl>
            )}
          />

          <Controller
            name="vesselCount"
            control={control}
            rules={{
              required: 'Vessel count is required',
              min: { value: 1, message: 'At least 1 vessel' },
              max: { value: 10, message: 'Maximum 10 vessels' },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                onChange={(e) => field.onChange(Number(e.target.value))}
                label="Number of Vessels"
                type="number"
                fullWidth
                error={!!errors.vesselCount}
                helperText={errors.vesselCount?.message}
                slotProps={{
                  htmlInput: { min: 1, max: 10 },
                }}
              />
            )}
          />

          <Controller
            name="entityName"
            control={control}
            rules={{ required: 'Entity name is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Entity Name"
                fullWidth
                error={!!errors.entityName}
                helperText={errors.entityName?.message}
              />
            )}
          />

          <Controller
            name="mohaAccountNumber"
            control={control}
            rules={{ required: 'MOHA account number is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="MOHA Account Number"
                fullWidth
                error={!!errors.mohaAccountNumber}
                helperText={errors.mohaAccountNumber?.message}
              />
            )}
          />

          <Controller
            name="initialEuaBalance"
            control={control}
            rules={{
              min: { value: 0, message: 'Balance cannot be negative' },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                onChange={(e) => field.onChange(Number(e.target.value))}
                label="Initial EUA Balance"
                type="number"
                fullWidth
                error={!!errors.initialEuaBalance}
                helperText={errors.initialEuaBalance?.message}
                slotProps={{
                  htmlInput: { min: 0 },
                }}
              />
            )}
          />

          <Controller
            name="expiresInDays"
            control={control}
            rules={{
              required: 'Expiry is required',
              min: { value: 1, message: 'At least 1 day' },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                onChange={(e) => field.onChange(Number(e.target.value))}
                label="Expires In (days)"
                type="number"
                fullWidth
                error={!!errors.expiresInDays}
                helperText={errors.expiresInDays?.message}
                slotProps={{
                  htmlInput: { min: 1 },
                }}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={!isValid || createMutation.isPending}
          startIcon={createMutation.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {createMutation.isPending ? 'Creating...' : 'Create Demo Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
